/**
 * Filters component for predictions table
 * Multi-select dropdowns and search input
 */

import { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Button,
  Autocomplete,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { ReviewStatus, PredictionFilters, Product, ExperimentVersion } from '../types';
import { useAppStore } from '../store/appStore';

interface PredictionsFiltersProps {
  filters: PredictionFilters;
  onFiltersChange: (filters: PredictionFilters) => void;
  availableFilters: {
    grades: string[];
    units: string[];
    reviewers: string[];
    assignees: string[];
  };
  onDownload?: () => void;
  isDownloading?: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const statusOptions = [
  { value: ReviewStatus.PENDING, label: 'Pending' },
  { value: ReviewStatus.IN_PROGRESS, label: 'In Progress' },
  { value: ReviewStatus.APPROVED, label: 'Approved' },
  { value: ReviewStatus.REJECTED, label: 'Rejected' },
];

export default function PredictionsFilters({
  filters,
  onFiltersChange,
  availableFilters,
  onDownload,
  isDownloading,
}: PredictionsFiltersProps) {
  const selectedProduct = useAppStore((state) => state.selectedProduct);
  const selectedExperiment = useAppStore((state) => state.selectedExperiment);
  const setProduct = useAppStore((state) => state.setProduct);
  const setExperiment = useAppStore((state) => state.setExperiment);

  // Debounced search state
  const [searchInput, setSearchInput] = useState(filters.searchQuery || '');

  // Sync searchInput when filters.searchQuery changes externally (e.g., clear all)
  useEffect(() => {
    setSearchInput(filters.searchQuery || '');
  }, [filters.searchQuery]);

  // Debounce search query updates
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (filters.searchQuery || '')) {
        onFiltersChange({
          ...filters,
          searchQuery: searchInput,
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleMultiSelectChange = (
    event: SelectChangeEvent<string[]>,
    field: keyof PredictionFilters
  ) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      [field]: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleClearAll = () => {
    onFiltersChange({
      grades: [],
      units: [],
      statuses: [],
      reviewers: [],
      assignees: [],
      searchQuery: '',
      tagsFilter: undefined,
    });
  };

  // Check if any filters are active
  const hasActiveFilters = !!(
    (filters.grades && filters.grades.length > 0) ||
    (filters.units && filters.units.length > 0) ||
    (filters.statuses && filters.statuses.length > 0) ||
    (filters.reviewers && filters.reviewers.length > 0) ||
    (filters.assignees && filters.assignees.length > 0) ||
    (filters.searchQuery && filters.searchQuery.trim() !== '') ||
    filters.tagsFilter
  );


  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
      {/* Product Selector */}
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Product</InputLabel>
        <Select
          value={selectedProduct}
          label="Product"
          onChange={(e) => setProduct(e.target.value as Product)}
        >
          <MenuItem value={Product.IM_MATH}>IM Math</MenuItem>
        </Select>
      </FormControl>

      {/* Experiment Selector */}
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Experiment Version</InputLabel>
        <Select
          value={selectedExperiment}
          label="Experiment Version"
          onChange={(e) => setExperiment(e.target.value as ExperimentVersion)}
        >
          <MenuItem value={ExperimentVersion.V0_0_0}>v0.0.0</MenuItem>
        </Select>
      </FormControl>

      {/* Vertical Separator */}
      <Typography
        sx={{
          color: 'divider',
          fontSize: '2.4rem',
          mx: 1,
          userSelect: 'none'
        }}
      >
        |
      </Typography>

      {/* Search */}
      <TextField
        label="Search"
        placeholder="Search lessons..."
        value={searchInput}
        onChange={handleSearchChange}
        sx={{
          minWidth: 200,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: searchInput?.trim() ? 'primary.main' : undefined,
              borderWidth: searchInput?.trim() ? 2 : undefined,
            },
          },
        }}
      />

      {/* Grade Filter */}
      <FormControl
        sx={{
          minWidth: 150,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: filters.grades?.length ? 'primary.main' : undefined,
              borderWidth: filters.grades?.length ? 2 : undefined,
            },
          },
        }}
      >
        <InputLabel>Grade</InputLabel>
        <Select
          multiple
          value={filters.grades || []}
          onChange={(e) => handleMultiSelectChange(e, 'grades')}
          input={<OutlinedInput label="Grade" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={`Grade ${value}`} size="small" />
              ))}
            </Box>
          )}
          MenuProps={MenuProps}
        >
          {availableFilters.grades.map((grade) => (
            <MenuItem key={grade} value={grade}>
              Grade {grade}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Unit Filter */}
      <FormControl
        sx={{
          minWidth: 150,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: filters.units?.length ? 'primary.main' : undefined,
              borderWidth: filters.units?.length ? 2 : undefined,
            },
          },
        }}
      >
        <InputLabel>Unit</InputLabel>
        <Select
          multiple
          value={filters.units || []}
          onChange={(e) => handleMultiSelectChange(e, 'units')}
          input={<OutlinedInput label="Unit" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
          MenuProps={MenuProps}
        >
          {availableFilters.units.map((unit) => (
            <MenuItem key={unit} value={unit}>
              {unit}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Status Filter */}
      <FormControl
        sx={{
          minWidth: 150,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: filters.statuses?.length ? 'primary.main' : undefined,
              borderWidth: filters.statuses?.length ? 2 : undefined,
            },
          },
        }}
      >
        <InputLabel>Status</InputLabel>
        <Select
          multiple
          value={filters.statuses || []}
          onChange={(e) => handleMultiSelectChange(e, 'statuses')}
          input={<OutlinedInput label="Status" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={statusOptions.find((s) => s.value === value)?.label}
                  size="small"
                />
              ))}
            </Box>
          )}
          MenuProps={MenuProps}
        >
          {statusOptions.map((status) => (
            <MenuItem key={status.value} value={status.value}>
              {status.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Reviewer Filter */}
      <Autocomplete
        multiple
        options={availableFilters.reviewers}
        value={filters.reviewers || []}
        onChange={(_, newValue) => {
          onFiltersChange({
            ...filters,
            reviewers: newValue,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Reviewer"
            placeholder="Search reviewers..."
            sx={{ minWidth: 220 }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option}
              size="small"
              {...getTagProps({ index })}
              key={option}
            />
          ))
        }
        ChipProps={{ size: 'small' }}
        sx={{
          minWidth: 220,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: filters.reviewers?.length ? 'primary.main' : undefined,
              borderWidth: filters.reviewers?.length ? 2 : undefined,
            },
          },
        }}
      />

      {/* Assignee Filter */}
      <Autocomplete
        multiple
        options={availableFilters.assignees}
        value={filters.assignees || []}
        onChange={(_, newValue) => {
          onFiltersChange({
            ...filters,
            assignees: newValue,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Assignee"
            placeholder="Search assignees..."
            sx={{ minWidth: 220 }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option}
              size="small"
              {...getTagProps({ index })}
              key={option}
            />
          ))
        }
        ChipProps={{ size: 'small' }}
        sx={{
          minWidth: 220,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: filters.assignees?.length ? 'primary.main' : undefined,
              borderWidth: filters.assignees?.length ? 2 : undefined,
            },
          },
        }}
      />

      {/* Tags Filter */}
      <FormControl
        sx={{
          minWidth: 150,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: filters.tagsFilter ? 'primary.main' : undefined,
              borderWidth: filters.tagsFilter ? 2 : undefined,
            },
          },
        }}
      >
        <InputLabel>Tags</InputLabel>
        <Select
          value={filters.tagsFilter || ''}
          label="Tags"
          onChange={(e) => {
            const value = e.target.value;
            onFiltersChange({
              ...filters,
              tagsFilter: value === '' ? undefined : (value as 'has_tags' | 'no_tags'),
            });
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="has_tags">Has Tags</MenuItem>
          <MenuItem value="no_tags">Has No Tags</MenuItem>
        </Select>
      </FormControl>

      {/* Clear Filters Button */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleClearAll}
        disabled={!hasActiveFilters}
        sx={{ marginLeft: 'auto' }}
      >
        Clear Filters
      </Button>

      {/* Download Button */}
      {onDownload && (
        <Button
          variant="outlined"
          size="small"
          onClick={onDownload}
          disabled={isDownloading}
          startIcon={<DownloadIcon />}
          sx={{ ml: 1 }}
        >
          Download
        </Button>
      )}
    </Box>
  );
}
